import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format } from "date-fns";
import { CalendarIcon, Plus, Trash, Upload, Image, Instagram, Facebook, Link2, ExternalLink, Heart, Users, User, Users2, UserRound, Briefcase, Headphones, GraduationCap, Building, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/common/UserAvatar";
import { 
  RelationshipType, 
  AnniversaryType, 
  FriendContext,
  LoveLanguage,
  ParentType,
  CommunicationStyle,
  ContactFormValues,
  CustomField
} from "@/types/dashboard";
import { Card } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { TagsSection, Tag } from "./contact-form-sections/TagsSection";
import { TagType } from "@/components/common/PreferenceTag";

const baseFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email").optional().or(z.literal('')),
  notes: z.string().optional(),
  photoUrl: z.string().optional(),
  phone: z.string().optional(),
  relationshipType: z.enum(["Spouse", "Partner", "Parent", "Sibling", "Friend", "Colleague", "Client", "Mentor", "Professional", "Friends with Benefits", "Other"]).optional(),
  instagramHandle: z.string().optional(),
  facebookHandle: z.string().optional(),
  isFavorite: z.boolean().optional(),
  // Extended fields for relationship types
  loveLanguage: z.string().optional(),
  firstDate: z.date().optional(),
  anniversaryDate: z.date().optional(),
  anniversaryType: z.string().optional(),
  friendshipStartDate: z.date().optional(),
  context: z.string().optional(),
  insideJokesOrMemories: z.string().optional(),
  lastHangoutDate: z.date().optional(),
  companyOrProject: z.string().optional(),
  lastInteractionDate: z.date().optional(),
  preferredCommunicationStyle: z.string().optional(),
  parentType: z.string().optional(),
  healthNotes: z.string().optional(),
  // Spouse specific fields
  weddingDate: z.date().optional(),
  marriageAnniversary: z.date().optional(),
  spousePreferences: z.string().optional(),
  // Additional fields
  birthdate: z.date().optional(),
  customRelationshipLabel: z.string().optional(),
});

const DynamicContactFormRefactored: React.FC<{
  initialValues?: Partial<ContactFormValues>;
  onSubmit: (values: ContactFormValues) => void;
  isSubmitting?: boolean;
}> = ({ initialValues = {}, onSubmit, isSubmitting = false }) => {
  const [customFields, setCustomFields] = useState<CustomField[]>(
    initialValues.customFields || []
  );
  const [tags, setTags] = useState<Tag[]>(
    initialValues.tags?.map(tag => ({
      id: tag.id || '',
      label: tag.label,
      type: (tag.type as TagType) || 'interest'
    })) || []
  );
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(initialValues.photoUrl || null);
  const [noteImages, setNoteImages] = useState<File[]>([]);
  const [noteImagePreviews, setNoteImagePreviews] = useState<string[]>([]);
  const [isInstagramConnected, setIsInstagramConnected] = useState(Boolean(initialValues.instagramHandle));
  const [isFacebookConnected, setIsFacebookConnected] = useState(Boolean(initialValues.facebookHandle));
  const [isSocialDialogOpen, setIsSocialDialogOpen] = useState(false);
  const [activeConnector, setActiveConnector] = useState<"instagram" | "facebook" | null>(null);
  const { toast } = useToast();

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(baseFormSchema),
    defaultValues: {
      name: "",
      email: "",
      notes: "",
      instagramHandle: "",
      facebookHandle: "",
      phone: "",
      relationshipType: "Other",
      isFavorite: false,
      // Initialize all relationship-specific fields
      loveLanguage: "",
      anniversaryType: "",
      context: "",
      insideJokesOrMemories: "",
      companyOrProject: "",
      preferredCommunicationStyle: "",
      parentType: "",
      healthNotes: "",
      spousePreferences: "",
      customRelationshipLabel: "",
      ...initialValues,
    },
  });

  const selectedRelationshipType = form.watch("relationshipType");

  const handleProfilePhotoUpload = (file: File) => {
    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Profile photo must be less than 10MB",
        variant: "destructive",
      });
      return;
    }

    setProfilePhoto(file);
    
    // Create a preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setProfilePhotoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleNoteImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles: File[] = [];
    const newPreviews: string[] = [];
    
    // Process each file
    Array.from(files).forEach(file => {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Images must be less than 10MB",
          variant: "destructive",
        });
        return;
      }
      
      newFiles.push(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push(e.target?.result as string);
        // Update state when we have all previews
        if (newPreviews.length === newFiles.length) {
          setNoteImages([...noteImages, ...newFiles]);
          setNoteImagePreviews([...noteImagePreviews, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeNoteImage = (index: number) => {
    const updatedImages = [...noteImages];
    const updatedPreviews = [...noteImagePreviews];
    
    updatedImages.splice(index, 1);
    updatedPreviews.splice(index, 1);
    
    setNoteImages(updatedImages);
    setNoteImagePreviews(updatedPreviews);
  };

  const handleAddCustomField = () => {
    const id = `custom-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    setCustomFields([...customFields, { id, label: "", value: "", isDate: false }]);
  };

  const handleCustomFieldChange = (index: number, field: Partial<CustomField>) => {
    const updated = [...customFields];
    updated[index] = { ...updated[index], ...field };
    setCustomFields(updated);
  };

  const handleRemoveCustomField = (index: number) => {
    setCustomFields(customFields.filter((_, i) => i !== index));
  };

  const handleAddTag = (label: string, type: TagType) => {
    const id = `tag-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    setTags([...tags, { id, label, type }]);
  };

  const handleRemoveTag = (id: string) => {
    setTags(tags.filter(tag => tag.id !== id));
  };

  const handleSubmit = (values: ContactFormValues) => {
    // In a real app, you would upload the images to a server here
    // and get back URLs to store in the form data
    
    // For demonstration purposes, we'll just add placeholder URLs based on the filenames
    // In a real implementation, you would upload these files to your backend/storage
    const formValues = {
      ...values,
      customFields: customFields,
      tags: tags.map(tag => ({
        id: tag.id,
        label: tag.label,
        type: tag.type
      })),
      avatarFile: profilePhoto,
      photoUrl: profilePhoto ? URL.createObjectURL(profilePhoto) : profilePhotoPreview,
      // Add any note image handling logic here if needed
    };
    
    console.log("Form submitting with values:", formValues);
    onSubmit(formValues);
  };

  const connectToSocial = (type: "instagram" | "facebook") => {
    setActiveConnector(type);
    setIsSocialDialogOpen(true);
  };

  const handleSocialConnect = () => {
    if (activeConnector === "instagram") {
      const instagramHandle = form.getValues("instagramHandle");
      if (instagramHandle) {
        setIsInstagramConnected(true);
        toast({
          title: "Instagram Connected",
          description: `Connected to Instagram account @${instagramHandle}`,
        });
      }
    } else if (activeConnector === "facebook") {
      const facebookHandle = form.getValues("facebookHandle");
      if (facebookHandle) {
        setIsFacebookConnected(true);
        toast({
          title: "Facebook Connected",
          description: `Connected to Facebook account ${facebookHandle}`,
        });
      }
    }
    
    setIsSocialDialogOpen(false);
  };

  const disconnectSocial = (type: "instagram" | "facebook") => {
    if (type === "instagram") {
      form.setValue("instagramHandle", "");
      setIsInstagramConnected(false);
      toast({
        title: "Instagram Disconnected",
        description: "Instagram account has been disconnected",
      });
    } else if (type === "facebook") {
      form.setValue("facebookHandle", "");
      setIsFacebookConnected(false);
      toast({
        title: "Facebook Disconnected",
        description: "Facebook account has been disconnected",
      });
    }
  };
  
  // Render different form fields based on relationship type
  const renderRelationshipFields = () => {
    switch (selectedRelationshipType) {
      case "Spouse":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Spouse Details</h3>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="weddingDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Wedding Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(new Date(field.value), "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value ? new Date(field.value) : undefined}
                          onSelect={date => {
                            field.onChange(date);
                            // Close popover when date is selected
                            document.body.click();
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="marriageAnniversary"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Marriage Anniversary</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(new Date(field.value), "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value ? new Date(field.value) : undefined}
                          onSelect={date => {
                            field.onChange(date);
                            // Close popover when date is selected
                            document.body.click();
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="spousePreferences"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Spouse Preferences & Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add any preferences or special notes about your spouse..."
                      className="h-20"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="loveLanguage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Love Language</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select love language" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Words of Affirmation">Words of Affirmation</SelectItem>
                      <SelectItem value="Acts of Service">Acts of Service</SelectItem>
                      <SelectItem value="Receiving Gifts">Receiving Gifts</SelectItem>
                      <SelectItem value="Quality Time">Quality Time</SelectItem>
                      <SelectItem value="Physical Touch">Physical Touch</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        );
        
      case "Partner":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Partner Details</h3>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="firstDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>First Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(new Date(field.value), "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value ? new Date(field.value) : undefined}
                          onSelect={(date) => field.onChange(date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="anniversaryDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Anniversary Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(new Date(field.value), "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value ? new Date(field.value) : undefined}
                          onSelect={(date) => field.onChange(date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="anniversaryType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Anniversary Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Dating">Dating</SelectItem>
                        <SelectItem value="Wedding">Wedding</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="loveLanguage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Love Language</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select love language" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Words of Affirmation">Words of Affirmation</SelectItem>
                        <SelectItem value="Acts of Service">Acts of Service</SelectItem>
                        <SelectItem value="Gifts">Gifts</SelectItem>
                        <SelectItem value="Quality Time">Quality Time</SelectItem>
                        <SelectItem value="Physical Touch">Physical Touch</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        );
        
      case "Friend":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Friendship Details</h3>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="friendshipStartDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Friendship Start Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(new Date(field.value), "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value ? new Date(field.value) : undefined}
                          onSelect={(date) => field.onChange(date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="context"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>How You Met</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select context" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="School">School</SelectItem>
                        <SelectItem value="Work">Work</SelectItem>
                        <SelectItem value="Online">Online</SelectItem>
                        <SelectItem value="Travel">Travel</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="insideJokesOrMemories"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Inside Jokes or Special Memories</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add any inside jokes or special memories..."
                      className="h-20"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="lastHangoutDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Last Hangout Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(new Date(field.value), "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ? new Date(field.value) : undefined}
                        onSelect={(date) => field.onChange(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        );
        
      case "Colleague":
      case "Client":
      case "Mentor":
      case "Professional":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Professional Details</h3>
            
            <FormField
              control={form.control}
              name="companyOrProject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company or Project</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter company or project name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="lastInteractionDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Last Interaction</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(new Date(field.value), "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value ? new Date(field.value) : undefined}
                          onSelect={(date) => field.onChange(date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="preferredCommunicationStyle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preferred Communication</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select communication style" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Email">Email</SelectItem>
                        <SelectItem value="Slack">Slack</SelectItem>
                        <SelectItem value="Zoom">Zoom</SelectItem>
                        <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        );
        
      case "Parent":
      case "Sibling":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Family Details</h3>
            
            {selectedRelationshipType === "Parent" && (
              <FormField
                control={form.control}
                name="parentType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Parent Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select parent type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Mother">Mother</SelectItem>
                        <SelectItem value="Father">Father</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            
            <FormField
              control={form.control}
              name="healthNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Health Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add any health notes or important information..."
                      className="h-20"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Basic Information</h3>
          
          <div className="flex flex-col items-center mb-6">
            <UserAvatar 
              src={profilePhotoPreview} 
              name={form.getValues().name || "Contact"}
              size="large"
              editable={true}
              onImageUpload={handleProfilePhotoUpload}
            />
            <p className="text-sm text-gray-500 mt-2">
              Click on the avatar to upload a photo
            </p>
          </div>
          
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input placeholder="Enter email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input placeholder="Enter phone number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="relationshipType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Relationship Type</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select relationship type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Spouse">
                      <div className="flex items-center">
                        <Heart className="w-4 h-4 mr-2 text-red-500" />
                        <span>Spouse</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Partner">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-2 text-pink-500" />
                        <span>Partner</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Parent">
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-2 text-blue-500" />
                        <span>Parent</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Sibling">
                      <div className="flex items-center">
                        <Users2 className="w-4 h-4 mr-2 text-green-500" />
                        <span>Sibling</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Friend">
                      <div className="flex items-center">
                        <UserRound className="w-4 h-4 mr-2 text-yellow-500" />
                        <span>Friend</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Friends with Benefits">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-2 text-purple-500" />
                        <span>Friends with Benefits</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Colleague">
                      <div className="flex items-center">
                        <Briefcase className="w-4 h-4 mr-2 text-indigo-500" />
                        <span>Colleague</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Client">
                      <div className="flex items-center">
                        <Headphones className="w-4 h-4 mr-2 text-cyan-500" />
                        <span>Client</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Mentor">
                      <div className="flex items-center">
                        <GraduationCap className="w-4 h-4 mr-2 text-amber-500" />
                        <span>Mentor</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Professional">
                      <div className="flex items-center">
                        <Building className="w-4 h-4 mr-2 text-slate-500" />
                        <span>Professional</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Other">
                      <div className="flex items-center">
                        <MoreHorizontal className="w-4 h-4 mr-2 text-gray-500" />
                        <span>Other</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {selectedRelationshipType === "Other" && (
            <FormField
              control={form.control}
              name="customRelationshipLabel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Custom Relationship Label</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter custom relationship type" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          
          <FormField
            control={form.control}
            name="birthdate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Birthdate</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(new Date(field.value), "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value ? new Date(field.value) : undefined}
                      onSelect={field.onChange}
                      disabled={(date) => date > new Date()}
                      initialFocus
                      captionLayout="dropdown-buttons"
                      fromYear={1900}
                      toYear={new Date().getFullYear()}
                    />
                  </PopoverContent>
                </Popover>
                <FormDescription>
                  Select a birthdate from the calendar
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        {selectedRelationshipType && renderRelationshipFields()}
        
        {/* Custom Fields */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Custom Fields</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddCustomField}
            >
              <Plus className="mr-1 h-4 w-4" />
              Add Field
            </Button>
          </div>
          
          {customFields.length > 0 ? (
            <div className="space-y-3">
              {customFields.map((field, index) => (
                <Card key={field.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 w-full">
                      <div>
                        <FormLabel className="text-sm">Field Label</FormLabel>
                        <Input
                          value={field.label}
                          onChange={(e) =>
                            handleCustomFieldChange(index, { label: e.target.value })
                          }
                          placeholder="Enter field name"
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <FormLabel className="text-sm">Field Value</FormLabel>
                        <Input
                          value={field.value}
                          onChange={(e) =>
                            handleCustomFieldChange(index, { value: e.target.value })
                          }
                          placeholder="Enter value"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveCustomField(index)}
                      className="ml-2"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No custom fields added yet. Click "Add Field" to create one.
            </p>
          )}
        </div>
        
        {/* Tags Section */}
        <TagsSection
          tags={tags}
          onAddTag={handleAddTag}
          onRemoveTag={handleRemoveTag}
        />
        
        {/* Notes */}
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Add any notes about this contact..."
                  className="h-32"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {/* Note Image Upload Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <FormLabel>Attach Images to Notes (max 10MB each)</FormLabel>
            <label htmlFor="note-images" className="cursor-pointer">
              <div className="flex items-center gap-2 text-sm text-primary hover:text-primary/80">
                <Upload className="h-4 w-4" />
                <span>Upload Images</span>
              </div>
              <input 
                id="note-images"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleNoteImageUpload}
              />
            </label>
          </div>
          
          {noteImagePreviews.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
              {noteImagePreviews.map((preview, index) => (
                <div key={index} className="relative group">
                  <img 
                    src={preview} 
                    alt={`Note image ${index + 1}`} 
                    className="w-full h-32 object-cover rounded-md"
                  />
                  <button
                    type="button"
                    onClick={() => removeNoteImage(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : "Save Contact"}
        </Button>
      </form>

      {/* Social Connection Dialog */}
      <Dialog open={isSocialDialogOpen} onOpenChange={setIsSocialDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activeConnector === "instagram" && <Instagram className="h-5 w-5 text-pink-600" />}
              {activeConnector === "facebook" && <Facebook className="h-5 w-5 text-blue-600" />}
              Connect to {activeConnector === "instagram" ? "Instagram" : "Facebook"}
            </DialogTitle>
            <DialogDescription>
              Enter your {activeConnector === "instagram" ? "Instagram" : "Facebook"} details to connect
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {activeConnector === "instagram" && (
              <FormField
                control={form.control}
                name="instagramHandle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Instagram Username</FormLabel>
                    <FormControl>
                      <div className="flex">
                        <span className="flex items-center px-3 bg-muted border border-r-0 border-input rounded-l-md text-sm text-muted-foreground">
                          @
                        </span>
                        <Input 
                          placeholder="username" 
                          className="rounded-l-none"
                          {...field}
                          onChange={(e) => {
                            // Remove @ if user types it
                            field.onChange(e.target.value.replace('@', ''));
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormDescription>
                      Enter your Instagram username without the @ symbol
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            
            {activeConnector === "facebook" && (
              <FormField
                control={form.control}
                name="facebookHandle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Facebook Username or Profile Name</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="username or profile name"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Enter your Facebook username or profile name
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
          
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => setIsSocialDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSocialConnect}>
              Connect
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default DynamicContactFormRefactored;